import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Heart, Activity, Shield, Leaf, CheckCircle, ArrowRight, Star, Award, FlaskConical, Clock, Sparkles, ShoppingCart, Truck, Ban, Pill, FlaskRound, BadgeCheck, MapPin } from 'lucide-react';
import axios from 'axios';
import BBCAudioModal from '../components/BBCAudioModal';
import CredibilityStrip from '../components/CredibilityStrip';
import RecognisedBy from '../components/RecognisedBy';
import BrandAmbassadorSection from '../components/BrandAmbassadorSection';
import ClinicalResearchSection from '../components/ClinicalResearchSection';
import PressBlogSocialSection from '../components/PressBlogSocialSection';
import ProductSpecsBlock from '../components/ProductSpecsBlock';
import ShopByGoal from '../components/ShopByGoal';
import MissGoaSponsorBanner from '../components/MissGoaSponsorBanner';
import LycaRadioBanner from '../components/LycaRadioBanner';
import ReviewsAndRatings from '../components/ReviewsAndRatings';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [product, setProduct] = useState(null);
  const [bbcOpen, setBbcOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      if (response.data.length > 0) {
        setProduct(response.data[0]);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching products:', error);
      }
    }
  };

  const benefits = [
    {
      icon: Activity,
      title: 'Made for an Active Life',
      description: 'A simple daily curcumin supplement for people who want to keep moving and keep doing the things they enjoy.',
    },
    {
      icon: Clock,
      title: 'Up to 8-Hour Active Support',
      description: 'Engineered with a patented bioavailability matrix so two capsules a day (one morning, one evening) keep working all day, while ordinary curcumin clears your system in under an hour.',
    },
    {
      icon: FlaskConical,
      title: 'Our Flagship Formula',
      description: 'Advanced BCM-95® curcumin enhanced with Turmeric Essential Oil for superior absorption — 95% Curcumin Complex, Vegan, Non-GMO, backed by 75+ peer-reviewed studies.',
    },
    {
      icon: Award,
      title: 'Built to Clinical-Trial Standard',
      description: 'GMP-certified manufacturing standards, 100% natural turmeric extract, no synthetic bio-enhancers, no fillers, no toxic solvents. The same standard demanded by clinical research — now in your daily routine.',
    },
  ];

  const features = [
    '100% Natural & Vegan',
    'Non-GMO',
    'Clinically Studied',
    'Up to 8 Hours Bioavailability',
    'GMP Certified',
    'No Toxic Solvents',
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'BCM-95® has transformed my daily routine. I feel more energetic and focused throughout the day.',
      rating: 5,
    },
    {
      name: 'Michael Chen',
      text: 'I&rsquo;ve noticed a great difference in my daily mobility and overall comfort after just a few weeks of taking BCM-95®.',
      rating: 5,
    },
    {
      name: 'Emma Wilson',
      text: 'Finally, a natural supplement that actually works! The bioavailability makes all the difference.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="BCM-95® UK | 7× More Absorbable Curcumin | IIY Phytoventra"
        description="Buy BCM-95® in the UK from IIY Phytoventra — patented Curcugreen® curcumin, 7× more bioavailable, no piperine, vegan, FREE Royal Mail 48 delivery."
        pathOverride="/"
      />
      {/* ============ Video Hero (full-bleed background) ============ */}
      <section className="relative overflow-hidden bg-emerald-950" data-testid="hero-section">
        {/* Full-bleed looping lifestyle video */}
        <video
          src="/lifestyle-hero.mp4"
          poster="/lifestyle-hero-poster.jpg"
          autoPlay loop muted playsInline preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-lifestyle-video"
        />
        {/* Legibility overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-950/55 to-emerald-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-emerald-950/30" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-10 sm:py-20 md:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="text-[11px] md:text-xs font-semibold tracking-[0.3em] uppercase text-amber-300 mb-3 md:mb-5 block">
              IIY Phytoventra &middot; Invest In Yourself &reg;
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.03] mb-3 md:mb-5 text-white font-heading drop-shadow-2xl">
              Keep moving.<br /><span className="text-amber-300">Keep doing what you love.</span>
            </h1>
            <p className="text-base md:text-xl leading-relaxed text-white/90 mb-6 md:mb-8 max-w-xl drop-shadow">
              Mobility support for active adults, powered by clinically researched <strong className="text-white">BCM-95®</strong> curcumin.
            </p>

            <div className="flex flex-wrap gap-3 mb-0 sm:mb-8">
              <button
                onClick={() => navigate('/products')}
                className="bg-amber-400 hover:bg-amber-300 text-emerald-950 rounded-full px-8 py-4 text-base font-bold shadow-2xl transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2"
                data-testid="hero-shop-button"
              >
                <ShoppingCart className="w-4 h-4" /> Shop BCM-95®
              </button>
              <button
                onClick={() => document.getElementById('shop-by-goal')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white rounded-full px-8 py-4 text-base font-semibold transition-all duration-300 inline-flex items-center gap-2"
                data-testid="hero-explore-button"
              >
                Explore Your Goals <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="hidden sm:flex flex-wrap gap-2.5" data-testid="hero-feature-chips">
              {[
                { icon: FlaskRound, label: 'Clinically studied nutrients' },
                { icon: Leaf, label: 'Natural & vegan-friendly' },
                { icon: MapPin, label: 'Made for the UK' },
                { icon: Truck, label: 'Free UK delivery' },
              ].map((chip) => (
                <span
                  key={chip.label}
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 rounded-full px-4 py-2 text-sm font-medium text-white"
                  data-testid={`hero-chip-${chip.label.split(' ')[0].toLowerCase()}`}
                >
                  <chip.icon className="w-4 h-4 text-amber-300" />
                  {chip.label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Trust strip */}
        <div className="relative z-10 border-t border-white/10 bg-emerald-950/80 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hero-trust-strip">
              {[
                { icon: FlaskRound, label: 'Clinically researched BCM-95® curcumin' },
                { icon: BadgeCheck, label: 'Quality you can trust' },
                { icon: Leaf, label: 'Natural & plant-based' },
                { icon: MapPin, label: 'Proudly made for the UK' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <item.icon className="w-5 h-5 text-amber-300 flex-shrink-0" />
                  <span className="text-xs md:text-sm font-medium text-white/90 leading-snug">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ Miss Goa International sponsorship (auto-expires 6 Sep 2026) ============ */}
      <MissGoaSponsorBanner />

      {/* ============ Lyca Radio broadcast (auto-expires 20 Sep 2026) ============ */}
      <LycaRadioBanner />

      {/* ============ Shop by Health Goal (multi-product range) ============ */}
      <ShopByGoal />

      {/* ============ Free Discovery Call — Practitioner highlight ============ */}
      <section className="py-20 md:py-28 bg-emerald-900 text-white overflow-hidden" data-testid="varsha-highlight-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative order-2 md:order-1">
            <div className="aspect-[4/5] max-w-sm rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img src="/varsha-soneji.jpg" alt="Mrs. Varsha Soneji, Medical Herbalist" className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <span className="text-[11px] font-bold tracking-[0.25em] uppercase text-amber-300 mb-4 block">Free Discovery Call</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4 leading-tight">Speak with our Medical Herbalist</h2>
            <p className="text-emerald-100/90 mb-6 leading-relaxed">
              Book a free 10&ndash;15 minute call with <strong className="text-white">Mrs. Varsha Soneji</strong>, Medical Herbalist with 37+ years&rsquo; experience, and discover how tailored botanical guidance can support your personal health goals.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/discovery-call')} className="bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold rounded-full px-7 py-3.5 transition-colors inline-flex items-center gap-2" data-testid="varsha-book-btn">
                Book Your Free Call <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ As Featured In - Credibility Strip ============ */}
      <CredibilityStrip onPlayBBC={() => setBbcOpen(true)} />

      <RecognisedBy />

      {/* ===== BBC Radio Humberside audio modal ===== */}
      <BBCAudioModal open={bbcOpen} onClose={() => setBbcOpen(false)} />

      {/* Recognition — help visitors recognise themselves before the science */}
      <section className="py-16 md:py-20 bg-white border-b border-stone-100" data-testid="recognition-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.28em] uppercase text-primary mb-4 block">
            Made for people like you
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-emerald-900 leading-tight mb-4">
            If you want to keep doing the things you love, this is for you.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Whether it&apos;s staying active, travelling, gardening or keeping up with family, BCM-95® is made for people who want to look after themselves and keep moving through every stage of life.
          </p>
        </div>
      </section>

      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-b from-amber-50/50 via-stone-50 to-white" data-testid="curcumin-feature-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Bottle photo with cinematic Ken-Burns motion + amber glow */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl ring-1 ring-amber-700/20">
                <img
                  src="/bcm95-bottle.jpg"
                  alt="BCM-95® Turmeric Root Extract"
                  className="w-full h-full object-cover"
                  style={{ animation: 'kenBurns 20s ease-in-out infinite alternate' }}
                  data-testid="curcumin-feature-image"
                />
                {/* Subtle gradient for text legibility on bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-black/10" />

                {/* Floating headline overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <span className="inline-block bg-amber-500/95 text-black text-[10px] md:text-[11px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full mb-3 shadow-lg">
                    Mobility &amp; Joints
                  </span>
                  <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight drop-shadow-2xl font-heading">
                    Keep moving. <span className="text-amber-300">Keep doing what you love.</span>
                  </h3>
                  <p className="text-white/90 text-sm md:text-base mt-2 max-w-md drop-shadow-lg">
                    Mobility support for active adults, powered by clinically researched BCM-95® curcumin.
                  </p>
                </div>
              </div>
              {/* Ambient amber glow */}
              <div className="absolute -inset-6 bg-gradient-to-br from-amber-300/20 via-transparent to-emerald-400/15 blur-3xl -z-10 animate-pulse" />
            </motion.div>

            {/* Right: Supporting copy */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="order-1 lg:order-2"
            >
              <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-amber-700 mb-4 block">
                Our Flagship &middot; The Science Inside
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading leading-[1.05]">
                Engineered for the body. <span className="text-primary">Trusted by science.</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Every BCM-95® capsule delivers a <strong className="text-foreground">95% Curcumin Complex</strong> enhanced with naturally derived <strong className="text-foreground">Turmeric Essential Oil</strong> — clinically studied to be <strong className="text-foreground">7&times; more bioavailable</strong> than ordinary curcumin.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3" data-testid="curcumin-fact-1">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-muted-foreground"><strong className="text-foreground">100% natural</strong> turmeric extract — no synthetic bio-enhancers, no piperine, no additives</span>
                </li>
                <li className="flex items-start gap-3" data-testid="curcumin-fact-2">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-muted-foreground">Patented BCM-95<sup>&reg;</sup> technology &middot; 75+ peer-reviewed clinical studies</span>
                </li>
                <li className="flex items-start gap-3" data-testid="curcumin-fact-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-muted-foreground">Vegan capsule &middot; Non-GMO &middot; GMP-certified manufacturing</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/products')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-4 text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2"
                data-testid="curcumin-feature-cta"
              >
                Shop Now
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>

          {/* Product Specifications & Key Benefits — locked compliance block */}
          <div className="mt-16 lg:mt-20">
            <ProductSpecsBlock />
          </div>
        </div>
      </section>

      {/* ============ Clinical Research Evidence Base ============ */}
      <ClinicalResearchSection />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              Why Choose IIY
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="benefits-title">
              Why People Choose BCM-95®
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Backed by over 75 published studies on the BCM-95® extract — clear reasons it stands apart from ordinary turmeric.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-hover transition-all duration-500 border border-border/50 group"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3 font-heading">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 gradient-hero">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              Highly Bioavailable
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="features-title">
              The Science of Superior Absorption
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              BCM-95® combines 95% curcumin with turmeric essential oil for enhanced bioavailability. Clinical studies show it remains active in your body for up to 8 hours.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
              {features.map((feature, index) => (
                <div key={feature} className="flex items-start gap-2" data-testid={`feature-${index}`}>
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              Customer Reviews
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="testimonials-title">
              Trusted by People Who Value Staying Active
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real words from people who&apos;ve made BCM-95® part of their daily routine.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-soft"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`${testimonial.name}-star-${i}`} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">"{testimonial.text}"</p>
                <p className="font-semibold">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="cta-title">
              Keep moving. Keep enjoying life.
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Make BCM-95® part of your daily routine — clinically researched curcumin from a brand that shows its proof.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2"
              data-testid="cta-shop-button"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ============ News, Press Releases & Blog (moved to bottom) ============ */}
      <PressBlogSocialSection />

      {/* ============ Brand Ambassador — Bella Glanville (moved to bottom) ============ */}
      <BrandAmbassadorSection />

      {/* ============ Google Reviews (5.0★) — bottom of homepage ============ */}
      <section className="bg-stone-50 border-t border-emerald-700/10 py-16 md:py-20" data-testid="home-reviews-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <ReviewsAndRatings withSchema={false} />
        </div>
      </section>
    </div>
  );
}